// ./src/components/features/Reminders/reminderTemplates.ts
//
// The {{variables}} a host can drop into a reminder's subject/body (kept in sync with the server's
// ReminderCore.buildTokens), plus the starter TEMPLATES shown as "ghost" cards when no reminder of that
// kind exists yet — tapping one pre-fills the add drawer.
import { Reminder, REMINDER_KIND, REMINDER_CHANNEL, REMINDER_SCHEDULE_KIND, EVENT_RSVP_STATUS } from "@/utils/api";
import { VariableMenuItem } from "@/components/generic/VariableMenu/VariableMenu";

/** The default RSVP-nudge audience: guests who haven't given a final yes/no. Kept in sync with the
 *  server (ReminderCore.DEFAULT_NUDGE_RSVP_STATUSES). */
export const DEFAULT_NUDGE_RSVP_STATUSES: string[] = [
  EVENT_RSVP_STATUS.INVITED,
  EVENT_RSVP_STATUS.VISITED,
  EVENT_RSVP_STATUS.MAYBE,
];

/** Variables offered in the "Insert variable" menu — substituted per recipient by the server. */
export const REMINDER_VARIABLES: VariableMenuItem[] = [
  { key: "greetingName", label: "Greeting (RSVP group or first name)", group: "guest", groupLabel: "Guest" },
  { key: "guestFirstName", label: "Guest first name", group: "guest", groupLabel: "Guest" },
  { key: "guestFullName", label: "Guest full name", group: "guest", groupLabel: "Guest" },
  { key: "rsvpGroupName", label: "Party name (e.g. Johnson Family; else first name)", group: "guest", groupLabel: "Guest" },
  { key: "eventName", label: "Event name", group: "event", groupLabel: "Event" },
  { key: "eventWhen", label: "Event date & time", group: "event", groupLabel: "Event" },
  { key: "eventLocation", label: "Event location", group: "event", groupLabel: "Event" },
  { key: "eventUrl", label: "Event page link", group: "event", groupLabel: "Event" },
  { key: "rsvpUrl", label: "RSVP link", group: "event", groupLabel: "Event" },
];

export interface ReminderPreset {
  presetId: string;
  description: string;
  iconName: string;
  /** A partial reminder used to seed the add-drawer form (never sent as-is). */
  reminder: Reminder;
}

const DAY = 1440;

/** Starter templates. Shown as ghost cards for any kind the host hasn't created a reminder of yet. */
export const REMINDER_PRESETS: ReminderPreset[] = [
  {
    presetId: "week-before",
    description: "A friendly heads-up one week out",
    iconName: "Bell",
    reminder: {
      name: "One week to go",
      kind: REMINDER_KIND.EVENT_APPROACHING,
      channel: REMINDER_CHANNEL.EMAIL,
      scheduleKind: REMINDER_SCHEDULE_KIND.BEFORE_EVENT,
      offsetMinutes: 7 * DAY,
      messageSubject: "One week until {{eventName}}!",
      messageBody:
        "Hi {{greetingName}},\n\nJust a reminder that {{eventName}} is one week away — {{eventWhen}}. We can't wait to see you there!",
    },
  },
  {
    presetId: "day-before",
    description: "A final nudge the day before",
    iconName: "Clock",
    reminder: {
      name: "Tomorrow's the day",
      kind: REMINDER_KIND.EVENT_APPROACHING,
      channel: REMINDER_CHANNEL.EMAIL,
      scheduleKind: REMINDER_SCHEDULE_KIND.BEFORE_EVENT,
      offsetMinutes: 1 * DAY,
      messageSubject: "Tomorrow: {{eventName}}",
      messageBody:
        "Hi {{greetingName}},\n\n{{eventName}} is tomorrow — {{eventWhen}}{{eventLocation}}. See you soon!",
    },
  },
  {
    presetId: "rsvp-nudge",
    description: "Chase non-responders every 3 days",
    iconName: "Send",
    reminder: {
      name: "RSVP nudge",
      kind: REMINDER_KIND.RSVP_NO_RESPONSE,
      channel: REMINDER_CHANNEL.EMAIL,
      scheduleKind: REMINDER_SCHEDULE_KIND.BEFORE_EVENT,
      offsetMinutes: 14 * DAY,
      repeatEveryDays: 3,
      audience: { rsvpStatus: DEFAULT_NUDGE_RSVP_STATUSES },
      messageSubject: "Will we see you at {{eventName}}?",
      messageBody:
        "Hi {{greetingName}},\n\nWe haven't heard from you yet — will you be joining us at {{eventName}} on {{eventWhen}}? Let us know here: {{rsvpUrl}}",
    },
  },
];

/** The presets whose kind has no existing reminder — the ghosts worth showing. */
export const absentKindPresets = (reminders: Reminder[]): ReminderPreset[] => {
  const kinds = new Set(reminders.map((r) => r.kind));

  return REMINDER_PRESETS.filter((p) => !kinds.has(p.reminder.kind));
};
